import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Building2, MapPin, Phone, Mail, BedDouble, Bath, Maximize, 
  IndianRupee, CheckCircle2, Star, Calendar, User, ChevronRight,
  Loader2, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PublicMicrositePage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadMicrosite();
  }, [slug]);

  const loadMicrosite = async () => {
    try {
      const response = await axios.get(`${API}/api/public/microsite/${slug}`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Microsite not found or not published yet.');
      } else {
        setError('Failed to load microsite. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) {
      toast.error('Please provide your name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/microsite/${data.microsite.id}/lead`, null, {
        params: leadForm
      });
      setSubmitted(true);
      toast.success('Thank you! We will contact you soon.');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on Request';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading microsite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold mb-2">Oops!</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { microsite, project, partner } = data;
  const content = microsite.content || {};
  const theme = microsite.theme || {};
  const settings = microsite.settings || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div 
        className="relative h-[60vh] min-h-[400px] bg-cover bg-center"
        style={{ 
          backgroundImage: content.heroImage 
            ? `url(${content.heroImage})` 
            : project?.images?.[0] 
              ? `url(${project.images[0]})` 
              : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12 max-w-6xl mx-auto">
          <Badge className="w-fit mb-4 bg-amber-500 text-white">
            {project?.status || 'New Launch'}
          </Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
            {content.headline || project?.name || 'Premium Property'}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-4">
            {content.tagline || project?.location || 'Prime Location'}
          </p>
          <div className="flex items-center gap-4 text-white/80">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {project?.location || 'Location'}
            </span>
            {project?.builder_name && (
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {project.builder_name}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Price & Highlights */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Starting from</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {formatPrice(project?.price_range?.min)}
                    </p>
                  </div>
                  <div className="flex gap-4">
                    {project?.bhk_types?.map((bhk, idx) => (
                      <div key={idx} className="text-center px-4 py-2 bg-slate-100 rounded-lg">
                        <BedDouble className="h-5 w-5 mx-auto text-slate-600 mb-1" />
                        <span className="text-sm font-medium">{bhk} BHK</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Highlights */}
                {content.highlights && content.highlights.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {content.highlights.slice(0, 6).map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description */}
            {(content.description || project?.description) && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4">About the Project</h2>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {content.description || project?.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Amenities */}
            {project?.amenities && project.amenities.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Amenities</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {project.amenities.map((amenity, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <Star className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {project?.images && project.images.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Gallery</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {project.images.slice(0, 6).map((img, idx) => (
                      <div key={idx} className="aspect-video bg-slate-200 rounded-lg overflow-hidden">
                        <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Contact Form */}
          <div className="space-y-6">
            {/* Lead Form */}
            <Card className="sticky top-4">
              <CardContent className="p-6">
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                    <p className="text-muted-foreground">
                      We've received your enquiry. Our team will contact you shortly.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-4">Interested? Let's Connect</h3>
                    <form onSubmit={handleSubmitLead} className="space-y-4">
                      <div>
                        <Input
                          placeholder="Your Name *"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Phone Number *"
                          value={leadForm.phone}
                          onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          placeholder="Email (Optional)"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={submitting}>
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Get a Callback <ChevronRight className="h-4 w-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </form>
                    <p className="text-xs text-muted-foreground text-center mt-4">
                      By submitting, you agree to be contacted regarding this property.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Partner Info */}
            {partner && settings.showPartnerInfo !== false && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Your Property Advisor</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{partner.user?.name || 'Property Expert'}</p>
                      <p className="text-sm text-muted-foreground">{partner.company_name || 'Channel Partner'}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {partner.user?.phone && (
                      <a href={`tel:${partner.user.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <Phone className="h-4 w-4" />
                        {partner.user.phone}
                      </a>
                    )}
                    {partner.user?.email && (
                      <a href={`mailto:${partner.user.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <Mail className="h-4 w-4" />
                        {partner.user.email}
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 className="h-6 w-6 text-amber-500" />
            <span className="text-xl font-bold">effyRealty</span>
          </div>
          <p className="text-slate-400 text-sm">
            Powered by effyRealty CRM
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicMicrositePage;
